import { useCallback, useEffect, useRef, useState } from 'react';
import type { PaceOfPlayEntry } from '@moment-golf/types';
import { holesCompletedForScoreCode } from '@moment-golf/shared';
import { useDataContext } from '../Context/useDataContext';
import CreateApi from '../Settings/appSettings';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

function formatDuration(ms: number): string {
    if (ms < 0) ms = 0;
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

export interface PaceDisplay {
    elapsedFormatted: string;
    projectedFormatted: string;
    isOOP: boolean;
    /** true when a pace clock has been started (startTime exists on the doc). */
    hasPace: boolean;
    /** true when the pace doc exists in the DB (even without a startTime). */
    hasDoc: boolean;
    startTime: string | undefined;
}

/**
 * Fetches the PaceOfPlay document for the current player's tee time and derives
 * elapsed / projected time strings. Delta-polls every 5 minutes.
 *
 * Returns `hasPace: false` when no pace clock has been started yet.
 */
export function usePaceOfPlay(): PaceDisplay {
    const api = CreateApi();
    const { eventData, scoreData, ownEventScore, currentRound, scoreCode } = useDataContext();

    const [paceDoc, setPaceDoc] = useState<PaceOfPlayEntry | null>(null);
    const [tick, setTick] = useState(0);

    const lastFetchedRef = useRef<string | null>(null);
    const paceDocRef = useRef<PaceOfPlayEntry | null>(null);
    paceDocRef.current = paceDoc;

    const eventID: string | undefined = (eventData as any)?._id;
    const teeTime: string | undefined =
        scoreData?.[0]?.roundScores?.[currentRound - 1]?.teeTime
        ?? ownEventScore?.roundScores?.[currentRound - 1]?.teeTime;
    const isOpponent = eventData?.scoreType?.toLowerCase() === 'opponent';
    const paceScoreCode = isOpponent ? scoreCode : null;

    const fetchPace = useCallback(
        async (full = false) => {
            if (!eventID || !teeTime || !currentRound) return;

            const params: Record<string, any> = {
                eventID,
                roundNumber: currentRound,
                teeTime,
                scoreCode: paceScoreCode ?? 'null',
            };

            if (!full && lastFetchedRef.current) {
                params.since = lastFetchedRef.current;
            }

            try {
                const res = await api.get('/ls/GetPaceOfPlay', { params });
                const docs: PaceOfPlayEntry[] = res.data;

                if (docs.length > 0) {
                    const doc = docs[0];
                    setPaceDoc(doc);
                    lastFetchedRef.current = doc.modifiedAt;
                } else if (full) {
                    setPaceDoc(null);
                }
            } catch (err) {
                console.warn('[usePaceOfPlay] fetch error', err);
            }
        },
        [eventID, teeTime, currentRound, paceScoreCode] // eslint-disable-line react-hooks/exhaustive-deps
    );

    useEffect(() => {
        lastFetchedRef.current = null;
        fetchPace(true);
    }, [eventID, teeTime, currentRound, paceScoreCode]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const interval = setInterval(() => fetchPace(false), POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchPace]);

    useEffect(() => {
        const interval = setInterval(() => setTick((t) => t + 1), 60_000);
        return () => clearInterval(interval);
    }, []);

    if (!paceDoc?.startTime) {
        return {
            elapsedFormatted: '—',
            projectedFormatted: '—',
            isOOP: false,
            hasPace: false,
            hasDoc: paceDoc !== null,
            startTime: undefined,
        };
    }

    const nowMs = Date.now() + tick * 0;
    const effectiveNowMs = paceDoc.endTime
        ? Math.min(nowMs, new Date(paceDoc.endTime).getTime())
        : nowMs;
    const startMs = new Date(paceDoc.startTime).getTime();
    const elapsedMs = effectiveNowMs - startMs;

    const totalHoles: number =
        (eventData?.rounds?.[currentRound - 1] as any)?.totalHoles ??
        eventData?.totalHoles ?? 18;

    const targetHours: number =
        (eventData?.rounds?.[currentRound - 1] as any)?.targetRoundLength ??
        (eventData as any)?.roundLengthTarget ?? 4;
    const targetMs = targetHours * 60 * 60 * 1000;

    const paceScores = isOpponent
        ? (ownEventScore ? [ownEventScore] : [])
        : (Array.isArray(scoreData) ? scoreData : []);

    const holesCompleted = holesCompletedForScoreCode(
        paceScores,
        scoreCode,
        currentRound,
    );

    const projectedMs =
        holesCompleted > 0 ? (elapsedMs / holesCompleted) * totalHoles : 0;

    const isOOP = projectedMs > 0 && projectedMs > targetMs;

    return {
        elapsedFormatted: formatDuration(elapsedMs),
        projectedFormatted: holesCompleted > 0 ? formatDuration(projectedMs) : '—',
        isOOP,
        hasPace: true,
        hasDoc: true,
        startTime: paceDoc.startTime,
    };
}
